import React, { useState, useEffect, useCallback } from "react";
import {
  Store, Plus, Trash2, FileText, Receipt, BarChart3, AlertTriangle, Boxes, Wallet,
} from "lucide-react";
import { Card, Button, Badge, Spinner, PageHeader, Input, Select, Modal, EmptyState } from "../lib/ui";
import { useT } from "../lib/i18n";
import { useToast } from "../lib/toast";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { rp, InvoiceField, InvoiceViewerModal } from "../components/purchasing";
import { JournalPreviewButton } from "../components/JournalPreview";

const HQ_ROLES = ["SUPER_ADMIN", "OWNER", "CENTRAL_PURCHASING"];

// ---------------- Create modal ----------------
function CreateModal({ open, onClose, onCreated, products, branches, isHQ, ownBranchId }) {
  const { t } = useT();
  const toast = useToast();
  const today = new Date().toISOString().slice(0, 10);
  const [branchId, setBranchId] = useState(ownBranchId || "");
  const [supplierName, setSupplierName] = useState("");
  const [invoiceNo, setInvoiceNo] = useState("");
  const [purchaseDate, setPurchaseDate] = useState(today);
  const [notes, setNotes] = useState("");
  const [image, setImage] = useState(null);
  const [items, setItems] = useState([{ item_type: "INVENTORY", product_id: "", name: "", unit: "", quantity: "", unit_price: "" }]);
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (!isHQ && ownBranchId) setBranchId(ownBranchId); }, [isHQ, ownBranchId]);

  const reset = () => {
    setSupplierName(""); setInvoiceNo(""); setPurchaseDate(today); setNotes(""); setImage(null);
    setItems([{ item_type: "INVENTORY", product_id: "", name: "", unit: "", quantity: "", unit_price: "" }]);
    if (isHQ) setBranchId("");
  };

  const setItem = (i, patch) => setItems((arr) => arr.map((it, idx) => (idx === i ? { ...it, ...patch } : it)));
  const addRow = (type) => setItems((arr) => [...arr, { item_type: type, product_id: "", name: "", unit: "", quantity: "", unit_price: "" }]);
  const delRow = (i) => setItems((arr) => arr.filter((_, idx) => idx !== i));

  const total = items.reduce((s, it) => s + (Number(it.quantity) || 0) * (Number(it.unit_price) || 0), 0);

  const submit = async () => {
    if (isHQ && !branchId) { toast.error(t("Select a branch")); return; }
    const valid = items.filter((it) => (it.item_type === "INVENTORY" ? it.product_id : it.name) && Number(it.quantity) > 0);
    if (!valid.length) { toast.error(t("Add at least one item with quantity")); return; }
    setSaving(true);
    try {
      await api.post("/purchasing/branch", {
        branch_id: branchId, supplier_name: supplierName || null, invoice_no: invoiceNo || null,
        purchase_date: purchaseDate, notes: notes || null, invoice_image: image,
        items: valid.map((it) => ({
          item_type: it.item_type, product_id: it.item_type === "INVENTORY" ? it.product_id : null,
          name: it.item_type === "EXPENSE" ? it.name : null, unit: it.unit || null,
          quantity: Number(it.quantity), unit_price: Number(it.unit_price) || 0,
        })),
      });
      toast.success(t("Branch purchase recorded"));
      reset(); onCreated(); onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("Something went wrong"));
    } finally { setSaving(false); }
  };

  return (
    <Modal open={open} onClose={onClose} size="xl" title={t("New Branch Purchase")}
      footer={<>
        <Button variant="ghost" onClick={onClose}>{t("Cancel")}</Button>
        <Button onClick={submit} loading={saving} data-testid="pc-branch-submit"><FileText size={16} /> {t("Confirm Purchase")}</Button>
      </>}>
      <div className="space-y-4">
        <div className="grid gap-3 sm:grid-cols-3">
          {isHQ ? (
            <Select label={t("Branch")} value={branchId} onChange={(e) => setBranchId(e.target.value)} data-testid="pc-branch-select">
              <option value="">{t("Select branch")}</option>
              {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
            </Select>
          ) : (
            <Input label={t("Branch")} value={branches.find((b) => b.id === branchId)?.name || t("Your branch")} disabled />
          )}
          <Input label={t("Supplier / Store")} value={supplierName} onChange={(e) => setSupplierName(e.target.value)} placeholder={t("e.g. Toko Sayur Pak Budi")} data-testid="pc-branch-supplier" />
          <Input label={t("Purchase Date")} type="date" value={purchaseDate} onChange={(e) => setPurchaseDate(e.target.value)} />
        </div>

        <div className="rounded-xl border border-line">
          <div className="grid grid-cols-[110px,1fr,80px,110px,80px,36px] gap-2 border-b border-line bg-stone-100 px-3 py-2 text-xs font-semibold uppercase text-ink-mute">
            <span>{t("Type")}</span><span>{t("Item")}</span><span className="text-right">{t("Qty")}</span><span className="text-right">{t("Unit Price")}</span><span className="text-right">{t("Total")}</span><span />
          </div>
          {items.map((it, i) => (
            <div key={i} className="grid grid-cols-[110px,1fr,80px,110px,80px,36px] items-center gap-2 px-3 py-2" data-testid={`pc-branch-item-${i}`}>
              <select className="cf-input !py-1.5 !text-xs" value={it.item_type} onChange={(e) => setItem(i, { item_type: e.target.value, product_id: "", name: "" })} data-testid={`pc-branch-item-type-${i}`}>
                <option value="INVENTORY">{t("Inventory")}</option>
                <option value="EXPENSE">{t("Expense")}</option>
              </select>
              {it.item_type === "INVENTORY" ? (
                <select className="cf-input !py-1.5 !text-sm" value={it.product_id} onChange={(e) => setItem(i, { product_id: e.target.value })} data-testid={`pc-branch-item-product-${i}`}>
                  <option value="">{t("Select product")}</option>
                  {products.map((p) => <option key={p.id} value={p.id}>{p.name} ({p.unit})</option>)}
                </select>
              ) : (
                <input className="cf-input !py-1.5 !text-sm" value={it.name} onChange={(e) => setItem(i, { name: e.target.value })} placeholder={t("e.g. Gas LPG, Air Galon")} data-testid={`pc-branch-item-name-${i}`} />
              )}
              <input className="cf-input !py-1.5 !text-right !text-sm" type="number" value={it.quantity} onChange={(e) => setItem(i, { quantity: e.target.value })} placeholder="0" data-testid={`pc-branch-item-qty-${i}`} />
              <input className="cf-input !py-1.5 !text-right !text-sm" type="number" value={it.unit_price} onChange={(e) => setItem(i, { unit_price: e.target.value })} placeholder="0" data-testid={`pc-branch-item-price-${i}`} />
              <span className="text-right text-sm font-semibold text-ink">{rp((Number(it.quantity) || 0) * (Number(it.unit_price) || 0))}</span>
              <button onClick={() => delRow(i)} disabled={items.length === 1} className="text-ink-mute hover:text-danger-text disabled:opacity-30"><Trash2 size={15} /></button>
            </div>
          ))}
          <div className="flex flex-wrap items-center justify-between gap-2 border-t border-line px-3 py-2">
            <div className="flex gap-2">
              <Button variant="ghost" size="sm" onClick={() => addRow("INVENTORY")} data-testid="pc-branch-add-inventory"><Boxes size={14} /> {t("Add Inventory")}</Button>
              <Button variant="ghost" size="sm" onClick={() => addRow("EXPENSE")} data-testid="pc-branch-add-expense"><Wallet size={14} /> {t("Add Expense")}</Button>
            </div>
            <p className="text-sm font-bold text-ink">{t("Total")}: <span className="text-primary">{rp(total)}</span></p>
          </div>
        </div>

        <div className="grid gap-3 sm:grid-cols-3">
          <Input label={t("Invoice Number")} value={invoiceNo} onChange={(e) => setInvoiceNo(e.target.value)} />
          <Input label={t("Notes")} value={notes} onChange={(e) => setNotes(e.target.value)} />
          <InvoiceField value={image} onChange={setImage} testid="pc-branch-invoice" />
        </div>
        <p className="rounded-lg bg-blue-50 px-3 py-2 text-xs text-blue-800">{t("Inventory items increase Branch Inventory. Expense items (Gas, Air Galon, service) are tracked as spending only — no inventory or HPP impact.")}</p>
      </div>
    </Modal>
  );
}

// ---------------- History tab ----------------
function HistoryTab({ products, branches, isHQ, ownBranchId }) {
  const { t } = useT();
  const toast = useToast();
  const [rows, setRows] = useState(null);
  const [branchFilter, setBranchFilter] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [invoiceId, setInvoiceId] = useState(null);
  const { hasPermission } = useAuth();
  const canCreate = hasPermission("create_purchase_branch");

  const load = useCallback(async () => {
    try {
      const params = isHQ && branchFilter ? { branch_id: branchFilter } : {};
      const { data } = await api.get("/purchasing/branch", { params });
      setRows(data.rows);
    } catch { toast.error(t("Failed to load")); }
  }, [t, toast, isHQ, branchFilter]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        {isHQ ? (
          <select className="cf-input !py-2 !text-sm sm:w-56" value={branchFilter} onChange={(e) => setBranchFilter(e.target.value)} data-testid="pc-branch-filter">
            <option value="">{t("All branches")}</option>
            {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
        ) : <div />}
        {canCreate && <Button onClick={() => setShowCreate(true)} data-testid="pc-branch-new"><Plus size={16} /> {t("New Purchase")}</Button>}
      </div>
      <Card>
        {!rows ? <div className="flex h-32 items-center justify-center"><Spinner className="h-7 w-7" /></div>
          : rows.length === 0 ? <EmptyState title={t("No purchases yet")} subtitle={t("Record your first branch purchase.")} />
          : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="pc-branch-table">
                <thead className="border-b border-line text-left text-xs uppercase tracking-wide text-ink-mute">
                  <tr><th className="py-2 pr-3">{t("Reference")}</th><th className="py-2 pr-3">{t("Date")}</th><th className="py-2 pr-3">{t("Branch")}</th><th className="py-2 pr-3">{t("Supplier")}</th><th className="py-2 pr-3 text-right">{t("Inventory")}</th><th className="py-2 pr-3 text-right">{t("Expense")}</th><th className="py-2 pr-3 text-right">{t("Total")}</th><th className="py-2 pr-3" /></tr>
                </thead>
                <tbody className="divide-y divide-line">
                  {rows.map((r) => (
                    <tr key={r.id} data-testid={`pc-branch-row-${r.reference_no}`}>
                      <td className="py-2.5 pr-3 font-mono text-xs font-semibold text-primary">{r.reference_no}{r.is_emergency && <Badge tone="warn" className="ml-1 !px-1.5 !py-0.5 !text-[10px]">{t("Emergency")}</Badge>}</td>
                      <td className="py-2.5 pr-3 text-xs text-ink-soft">{(r.purchase_date || r.created_at || "").slice(0, 10)}</td>
                      <td className="py-2.5 pr-3 text-ink">{r.branch_name}</td>
                      <td className="py-2.5 pr-3 text-ink-soft">{r.supplier_name || "—"}</td>
                      <td className="py-2.5 pr-3 text-right text-ink-soft">{rp(r.inventory_value)}</td>
                      <td className="py-2.5 pr-3 text-right text-ink-soft">{rp(r.expense_value)}</td>
                      <td className="py-2.5 pr-3 text-right font-semibold text-ink">{rp(r.total_value)}</td>
                      <td className="py-2.5 pr-3 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <JournalPreviewButton module="purchases" docId={r.id} />
                          {r.has_invoice ? <Button variant="ghost" size="sm" onClick={() => setInvoiceId(r.id)} data-testid={`pc-branch-invoice-${r.reference_no}`}><Receipt size={14} /> {t("View Invoice")}</Button> : <span className="text-xs text-ink-mute">{t("No invoice")}</span>}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
      </Card>
      <CreateModal open={showCreate} onClose={() => setShowCreate(false)} onCreated={load} products={products} branches={branches} isHQ={isHQ} ownBranchId={ownBranchId} />
      <InvoiceViewerModal purchaseId={invoiceId} open={!!invoiceId} onClose={() => setInvoiceId(null)} />
    </div>
  );
}

// ---------------- Dashboard tab ----------------
function DashboardTab() {
  const { t } = useT();
  const [dash, setDash] = useState(null);
  useEffect(() => { api.get("/purchasing/dashboard/summary").then(({ data }) => setDash(data)).catch(() => {}); }, []);
  if (!dash) return <div className="flex h-40 items-center justify-center"><Spinner className="h-7 w-7" /></div>;
  const perBranch = dash.branch?.per_branch || [];
  return (
    <Card data-testid="pc-branch-dashboard">
      <h3 className="mb-3 flex items-center gap-2 font-heading font-bold text-ink"><BarChart3 size={18} /> {t("Branch Spending (This Month)")}</h3>
      {perBranch.length === 0 ? <EmptyState title={t("No branch purchases this month")} /> : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b border-line text-left text-xs uppercase text-ink-mute"><tr><th className="py-2 pr-3">{t("Branch")}</th><th className="py-2 pr-3 text-center">{t("Purchases")}</th><th className="py-2 pr-3 text-center">{t("Emergency")}</th><th className="py-2 pr-3 text-right">{t("Monthly Spending")}</th></tr></thead>
            <tbody className="divide-y divide-line">
              {perBranch.map((b, i) => (
                <tr key={i}>
                  <td className="py-2.5 pr-3 font-medium text-ink">{b.branch_name}</td>
                  <td className="py-2.5 pr-3 text-center">{b.purchases}</td>
                  <td className="py-2.5 pr-3 text-center">{b.emergency > 0 ? <Badge tone="warn">{b.emergency}</Badge> : <span className="text-ink-mute">0</span>}</td>
                  <td className="py-2.5 pr-3 text-right font-semibold text-ink">{rp(b.spend)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

// ---------------- Page ----------------
export default function PembelianCabang() {
  const { t } = useT();
  const { user } = useAuth();
  const isHQ = HQ_ROLES.includes(user?.role);
  const [tab, setTab] = useState("history");
  const [products, setProducts] = useState([]);
  const [branches, setBranches] = useState([]);

  useEffect(() => {
    api.get("/products", { params: { active_only: true } }).then(({ data }) => {
      const list = Array.isArray(data) ? data : data.rows || [];
      setProducts(list.filter((p) => p.track_inventory !== false));
    }).catch(() => {});
    api.get("/branches").then(({ data }) => setBranches(Array.isArray(data) ? data : data.rows || [])).catch(() => {});
  }, []);

  const tabs = [{ id: "history", label: t("Purchases"), icon: Receipt }, { id: "dashboard", label: t("Dashboard"), icon: BarChart3 }];

  return (
    <div data-testid="pembelian-cabang-page">
      <PageHeader title={t("Branch Purchase")} subtitle={t("Branch-level purchases & operational expenses")} icon={Store} />
      <div className="mb-5 inline-flex rounded-xl border border-line bg-white p-1">
        {tabs.map((tb) => (
          <button key={tb.id} data-testid={`pc-branch-tab-${tb.id}`} onClick={() => setTab(tb.id)}
            className={`flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold transition-colors ${tab === tb.id ? "bg-primary text-white shadow-sm" : "text-ink-soft hover:bg-stone-100"}`}>
            <tb.icon size={16} /> {tb.label}
          </button>
        ))}
      </div>
      {tab === "history" ? <HistoryTab products={products} branches={branches} isHQ={isHQ} ownBranchId={user?.branch_id} /> : <DashboardTab />}
    </div>
  );
}
